import React, { useState, useEffect } from 'react';
import { X, Zap, Calendar, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BoostModal = ({ vehicle, onClose, onSuccess }) => {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/api/boost/packages`);
      setPackages(response.data.packages);
      if (response.data.packages.length > 0) {
        setSelectedPackage(response.data.packages[0].package_id);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Failed to load boost packages');
    }
  };

  const handleBoostPurchase = async () => {
    if (!selectedPackage) {
      toast.error('Please select a package');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token') || document.cookie.split('session_token=')[1]?.split(';')[0];
      
      if (!token) {
        toast.error('Please login to boost listings');
        setLoading(false);
        return;
      }
      
      console.log('Initializing boost purchase:', {
        vehicle_id: vehicle.vehicle_id,
        package_id: selectedPackage
      });
      
      const response = await axios.post(
        `${API}/api/boost/purchase`,
        {
          vehicle_id: vehicle.vehicle_id,
          package_id: selectedPackage
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );

      console.log('Boost purchase initialized:', response.data);

      const { authorization_url, reference, access_code } = response.data;

      // Open Paystack payment
      const PaystackPop = window.PaystackPop;
      
      if (!PaystackPop) {
        console.log('PaystackPop not loaded, redirecting to authorization URL');
        if (authorization_url) {
          window.location.href = authorization_url;
        } else {
          toast.error('Payment system not available');
          setLoading(false);
        }
        return;
      }

      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : {};
      const userEmail = user.email || 'dealer@jamohautos.com';

      // Get selected package data FIRST
      const selectedPackageData = packages.find(p => p.package_id === selectedPackage);
      if (!selectedPackageData) {
        toast.error('Invalid package selected');
        setLoading(false);
        return;
      }

      console.log('Opening Paystack popup for:', userEmail);
      console.log('Reference:', reference);
      console.log('Amount:', selectedPackageData.price * 100);
      console.log('Package:', selectedPackageData.name);

      const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;
      
      if (!PAYSTACK_PUBLIC_KEY || PAYSTACK_PUBLIC_KEY === 'pk_test_your_paystack_public_key_here') {
        toast.error('Payment system not configured. Please contact support.');
        setLoading(false);
        return;
      }

      // Try to open Paystack popup
      try {
        const handler = PaystackPop.setup({
          key: PAYSTACK_PUBLIC_KEY,
          email: userEmail,
          amount: selectedPackageData.price * 100,
          currency: 'NGN',
          ref: reference,
          callback: async (response) => {
            console.log('✅ Paystack callback triggered!');
            console.log('Payment reference:', response.reference);
            
            // Verify payment
            try {
              console.log('Calling verification endpoint...');
              const verifyResponse = await axios.post(
                `${API}/api/boost/verify/${response.reference}`,
                {},
                {
                  headers: { Authorization: `Bearer ${token}` },
                  withCredentials: true
                }
              );

              console.log('✅ Verification successful:', verifyResponse.data);
              toast.success('Boost activated successfully! Your vehicle is now featured.');
              
              // Refresh data and close modal
              setTimeout(() => {
                console.log('Refreshing dealer data and closing modal...');
                onSuccess();
                onClose();
              }, 1500);
            } catch (error) {
              console.error('❌ Boost verification error:', error);
              console.error('Error response:', error.response?.data);
              toast.error(error.response?.data?.detail || 'Boost activation failed. Please contact support.');
              setLoading(false);
            }
          },
          onClose: () => {
            setLoading(false);
          }
        });

        handler.openIframe();
      } catch (popupError) {
        console.error('Paystack popup setup error:', popupError);
        
        // Fallback: Redirect to authorization URL
        if (authorization_url) {
          window.location.href = authorization_url;
        } else {
          toast.error(`Payment window failed: ${popupError.message || 'Unknown error'}`);
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Boost purchase error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to initialize boost purchase';
      toast.error(`Boost Error: ${errorMessage}`);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="glass rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex justify-between items-center sticky top-0 glass">
          <div className="flex items-center space-x-3">
            <Zap className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-heading font-bold">Boost Listing</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-sm transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Vehicle Info */}
        <div className="p-6 border-b border-white/10">
          <h3 className="font-heading font-bold text-lg mb-2">
            {vehicle.year} {vehicle.make} {vehicle.model}
          </h3>
          <p className="text-sm text-textSecondary">
            Boost this vehicle to appear at the top of search results and attract more buyers
          </p>
        </div>

        {/* Packages */}
        <div className="p-6">
          <h3 className="font-heading font-bold mb-4">Select Boost Package</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {packages.map((pkg) => (
              <div
                key={pkg.package_id}
                onClick={() => setSelectedPackage(pkg.package_id)}
                className={`p-6 rounded-sm border-2 cursor-pointer transition-all ${
                  selectedPackage === pkg.package_id
                    ? 'border-primary bg-primary/10'
                    : 'border-white/10 hover:border-primary/50'
                }`}
              >
                <div className="flex items-center space-x-2 mb-3">
                  <Calendar className="w-5 h-5 text-primary" />
                  <span className="font-heading font-bold">{pkg.name}</span>
                </div>
                <div className="flex items-baseline space-x-1 mb-2">
                  <DollarSign className="w-4 h-4 text-green-500" />
                  <span className="text-2xl font-heading font-bold text-green-500">
                    ₦{pkg.price.toLocaleString()}
                  </span>
                </div>
                <p className="text-xs text-textSecondary">
                  {pkg.duration_days} days of premium placement
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits */}
        <div className="p-6 bg-surfaceHighlight">
          <h4 className="font-bold mb-3">Boost Benefits:</h4>
          <ul className="space-y-2 text-sm text-textSecondary">
            <li className="flex items-start space-x-2">
              <Zap className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <span>Appear at the top of all marketplace listings</span>
            </li>
            <li className="flex items-start space-x-2">
              <Zap className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <span>Featured badge on your vehicle listing</span>
            </li>
            <li className="flex items-start space-x-2">
              <Zap className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <span>Increased visibility to potential buyers</span>
            </li>
            <li className="flex items-start space-x-2">
              <Zap className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <span>Higher chances of quick sale</span>
            </li>
          </ul>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-6 py-3 rounded-sm hover:bg-white/10 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleBoostPurchase}
            disabled={loading || !selectedPackage}
            className="bg-primary hover:bg-primaryHover text-white px-8 py-3 rounded-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading ? (
              <span>Processing...</span>
            ) : (
              <>
                <Zap className="w-5 h-5" />
                <span>Boost Now</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BoostModal;
